import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  Package2, Loader2, User, Tag, Palette, Layers, ArrowLeft,
  ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import LanguageSelector from '@/components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicStash() {
  const { userId } = useParams();
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const navigate = useNavigate();
  
  const [stash, setStash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    loadPublicStash();
  }, [userId, i18n.language]);

  const loadPublicStash = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/stash/${userId}/public?lang=${i18n.language}`);
      setStash(response.data);
    } catch (err) {
      console.error('Failed to load public stash:', err);
      if (err.response?.status === 404) {
        setError(isEnglish 
          ? 'This stash is private or does not exist.' 
          : 'Tämä varasto on yksityinen tai sitä ei ole olemassa.');
      } else {
        setError(isEnglish ? 'Failed to load stash' : 'Varaston lataus epäonnistui');
      }
    } finally {
      setLoading(false);
    }
  };

  // Get yarn image
  const getYarnImage = (yarn) => {
    if (yarn.images && yarn.images.length > 0) return yarn.images[0];
    return yarn.image_url || null;
  };

  // Colors for stats
  const COLORS = [
    'bg-primary', 'bg-secondary', 'bg-accent', 'bg-blue-500', 'bg-purple-500',
    'bg-pink-500', 'bg-orange-500', 'bg-teal-500'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">
            {isEnglish ? 'Loading stash...' : 'Ladataan varastoa...'}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white">
        <header className="py-6 px-4 border-b border-stone-100 bg-white/80 backdrop-blur-md">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-stone-800 hover:text-primary transition-colors"
            >
              <Package2 className="h-8 w-8 text-primary" />
              <span className="text-2xl font-heading font-bold">Even Rounds</span>
            </button>
            <LanguageSelector variant="ghost" />
          </div>
        </header>
        
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center px-4">
            <Package2 className="h-24 w-24 text-muted-foreground/30 mx-auto mb-6" />
            <h1 className="text-2xl font-heading font-bold mb-4">
              {isEnglish ? 'Stash Not Found' : 'Varastoa Ei Löytynyt'}
            </h1>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate('/')} className="rounded-full">
              {isEnglish ? 'Go Home' : 'Etusivulle'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white">
      {/* Header */}
      <header className="py-6 px-4 border-b border-stone-100 bg-white/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-stone-800 hover:text-primary transition-colors"
          >
            <Package2 className="h-8 w-8 text-primary" />
            <span className="text-2xl font-heading font-bold">Even Rounds</span>
          </button>
          <div className="flex items-center gap-4">
            <LanguageSelector variant="ghost" />
            <Button 
              onClick={() => navigate('/')} 
              variant="outline" 
              className="rounded-full"
            >
              {isEnglish ? 'Get Started' : 'Aloita'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-12">
        {/* User Profile Section */}
        <div className="text-center mb-12">
          {stash.user.picture ? (
            <img 
              src={stash.user.picture} 
              alt={stash.user.display_name}
              className="h-24 w-24 rounded-full mx-auto mb-4 border-4 border-white shadow-lg object-cover"
            />
          ) : (
            <div className="h-24 w-24 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-lg">
              <User className="h-12 w-12 text-primary" />
            </div>
          )}
          <h1 className="text-3xl md:text-4xl font-heading font-extrabold mb-2">
            {stash.user.display_name || (isEnglish ? 'Yarn Lover' : 'Lankahullu')}
          </h1>
          {stash.user.bio && (
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              {stash.user.bio}
            </p>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="rounded-2xl bg-white border border-stone-100 p-5 text-center shadow-sm">
            <span className="text-3xl font-heading font-bold text-primary">
              {stash.stats.total_skeins}
            </span>
            <p className="text-sm text-muted-foreground mt-1">
              {isEnglish ? 'Total Skeins' : 'Kerät Yhteensä'}
            </p>
          </div>
          <div className="rounded-2xl bg-white border border-stone-100 p-5 text-center shadow-sm">
            <span className="text-3xl font-heading font-bold text-secondary">
              {stash.stats.unique_yarns}
            </span>
            <p className="text-sm text-muted-foreground mt-1">
              {isEnglish ? 'Unique Yarns' : 'Eri Lankoja'}
            </p>
          </div>
          <div className="rounded-2xl bg-white border border-stone-100 p-5 text-center shadow-sm">
            <span className="text-3xl font-heading font-bold text-accent">
              {stash.stats.top_brands?.length || 0}
            </span>
            <p className="text-sm text-muted-foreground mt-1">
              {isEnglish ? 'Brands' : 'Merkkejä'}
            </p>
          </div>
          <div className="rounded-2xl bg-white border border-stone-100 p-5 text-center shadow-sm">
            <span className="text-3xl font-heading font-bold text-purple-500">
              {stash.stats.top_colors?.length || 0}
            </span>
            <p className="text-sm text-muted-foreground mt-1">
              {isEnglish ? 'Colors' : 'Värejä'}
            </p>
          </div>
        </div>

        {/* Stats Details */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {/* Top Brands */}
          {stash.stats.top_brands?.length > 0 && (
            <div className="rounded-2xl bg-white border border-stone-100 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Tag className="h-5 w-5 text-primary" />
                <h3 className="font-heading font-semibold">
                  {isEnglish ? 'Top Brands' : 'Suosituimmat Merkit'}
                </h3>
              </div>
              <div className="space-y-2">
                {stash.stats.top_brands.map((brand, i) => (
                  <div key={brand.name} className="flex items-center justify-between">
                    <span className="text-sm truncate">{brand.name}</span>
                    <span className={`px-2 py-0.5 rounded-full text-white text-xs ${COLORS[i % COLORS.length]}`}>
                      {brand.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Weight Distribution */}
          {stash.stats.weights?.length > 0 && (
            <div className="rounded-2xl bg-white border border-stone-100 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Layers className="h-5 w-5 text-secondary" />
                <h3 className="font-heading font-semibold">
                  {isEnglish ? 'By Weight' : 'Paksuuden Mukaan'}
                </h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {stash.stats.weights.slice(0, 6).map((weight, i) => (
                  <span 
                    key={weight.name}
                    className={`px-3 py-1 rounded-full text-white text-xs font-medium ${COLORS[i % COLORS.length]}`}
                  >
                    {weight.name} ({weight.count})
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Top Colors */}
          {stash.stats.top_colors?.length > 0 && (
            <div className="rounded-2xl bg-white border border-stone-100 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Palette className="h-5 w-5 text-accent" />
                <h3 className="font-heading font-semibold">
                  {isEnglish ? 'Top Colors' : 'Suosituimmat Värit'}
                </h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {stash.stats.top_colors.map((color) => (
                  <span 
                    key={color.name}
                    className="px-3 py-1 rounded-full bg-stone-100 text-stone-700 text-xs"
                  >
                    {color.name} ({color.count})
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Yarn Gallery */}
        <div className="mb-8">
          <h2 className="text-2xl font-heading font-bold mb-6">
            {isEnglish ? 'Yarn Collection' : 'Lankakokoelma'}
            {stash.total_yarns > 50 && (
              <span className="text-base font-normal text-muted-foreground ml-2">
                ({isEnglish ? `Showing 50 of ${stash.total_yarns}` : `Näytetään 50/${stash.total_yarns}`})
              </span>
            )}
          </h2>
          
          {stash.yarns?.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {stash.yarns.map((yarn) => {
                const image = getYarnImage(yarn);
                return (
                  <div
                    key={yarn.yarn_id}
                    className="group rounded-2xl overflow-hidden bg-white border border-stone-100 shadow-sm hover:shadow-lg transition-all"
                  >
                    <div 
                      className="aspect-square bg-gradient-to-br from-rose-100 to-orange-100 relative overflow-hidden cursor-pointer"
                      onClick={() => {
                        if (image) {
                          setLightboxImage(image);
                          setLightboxOpen(true);
                        }
                      }}
                    >
                      {image ? (
                        <img 
                          src={image} 
                          alt={`${yarn.brand} - ${yarn.color}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package2 className="h-12 w-12 text-primary/40" />
                        </div>
                      )}
                      {yarn.quantity > 1 && (
                        <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-primary text-white text-xs font-bold">
                          ×{yarn.quantity}
                        </span>
                      )}
                    </div>
                    <div className="p-3">
                      <h4 className="font-medium text-sm truncate">
                        {yarn.brand || (isEnglish ? 'Unknown' : 'Tuntematon')}
                      </h4>
                      <p className="text-xs text-muted-foreground truncate">
                        {yarn.color || yarn.weight || ''}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 rounded-2xl bg-white border border-stone-100">
              <Package2 className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">
                {isEnglish ? 'No yarns to display' : 'Ei lankoja näytettäväksi'}
              </p>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="text-center py-12 rounded-3xl bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20">
          <h3 className="text-2xl font-heading font-bold mb-3">
            {isEnglish ? 'Start Your Own Stash' : 'Aloita Oma Varastosi'}
          </h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            {isEnglish 
              ? 'Join Even Rounds and organize your yarn collection beautifully!' 
              : 'Liity Even Roundsiin ja järjestä lankakokoelmasi kauniisti!'}
          </p>
          <Button 
            onClick={() => navigate('/')} 
            className="rounded-full bg-primary text-primary-foreground px-8"
          >
            {isEnglish ? 'Get Started Free' : 'Aloita Ilmaiseksi'}
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-stone-100 bg-white mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Package2 className="h-6 w-6 text-primary" />
            <span className="text-xl font-heading font-bold">Even Rounds</span>
          </div>
          <p className="text-sm text-stone-500">
            © 2026 Even Rounds. {isEnglish ? 'Made with love for yarn crafters.' : 'Tehty rakkaudella lankahulluille.'}
          </p>
        </div>
      </footer>

      {/* Lightbox */}
      {lightboxOpen && lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
          >
            <span className="sr-only">Close</span>
            ×
          </button>
          <img
            src={lightboxImage}
            alt="Yarn"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
